import type { ReactNode } from "react";
import { cn } from "@/utils/cn";

export type SettingsNavItem = {
  id: string;
  label: string;
  description?: string;
  icon?: ReactNode;
};

interface SettingsNavProps {
  items: SettingsNavItem[];
  activeId: string;
  onSelect: (id: string) => void;
  className?: string;
  "aria-label"?: string;
}

export function SettingsNav({
  items,
  activeId,
  onSelect,
  className,
  "aria-label": ariaLabel = "Settings sections",
}: SettingsNavProps) {
  return (
    <nav className={cn(className)} aria-label={ariaLabel}>
      <ul className="flex gap-1 overflow-x-auto pb-1 lg:flex-col lg:overflow-visible lg:pb-0">
        {items.map((item) => {
          const isActive = item.id === activeId;
          return (
            <li key={item.id} className="shrink-0 lg:shrink">
              <button
                type="button"
                id={`settings-nav-${item.id}`}
                aria-current={isActive ? "page" : undefined}
                aria-controls={`settings-panel-${item.id}`}
                onClick={() => onSelect(item.id)}
                className={cn(
                  "flex w-full items-start gap-3 rounded-lg px-3 py-2.5 text-left transition-colors",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40",
                  isActive
                    ? "bg-primary-50 text-primary-800 dark:bg-primary-950/50 dark:text-primary-200"
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800/60 dark:hover:text-slate-100",
                )}
              >
                {item.icon && (
                  <span
                    className={cn(
                      "mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md",
                      isActive
                        ? "bg-primary-100 text-primary-700 dark:bg-primary-900/60 dark:text-primary-300"
                        : "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400",
                    )}
                    aria-hidden
                  >
                    {item.icon}
                  </span>
                )}
                <span className="min-w-0">
                  <span className="block text-sm font-medium">
                    {item.label}
                  </span>
                  {item.description && (
                    <span className="mt-0.5 hidden text-xs leading-snug text-slate-500 dark:text-slate-400 lg:block">
                      {item.description}
                    </span>
                  )}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
